import { createContext, useState, useMemo, useContext, useCallback } from 'react';

const InventariosContext = createContext();

const BASE = process.env.REACT_APP_SERVICE_ENDPOINT;

export function InventariosProvider(props) {
    const tokenSessionObject = JSON.parse(sessionStorage.getItem('token'));
    const token = tokenSessionObject?.token;

    // ── Data ──
    const [almacenes, setAlmacenes] = useState([]);
    const [selectedAlmacen, setSelectedAlmacen] = useState(null);
    const [existencias, setExistencias] = useState([]);
    const [skuFilter, setSkuFilter] = useState('');

    // ── Loading ──
    const [loadingAlmacenes, setLoadingAlmacenes] = useState(false);
    const [loadingExistencias, setLoadingExistencias] = useState(false);
    const [saving, setSaving] = useState(false);

    // ── Snackbar ──
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);
    const closeSnackbar = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    // ── Dialogs ──
    const [dialogCreate, setDialogCreate] = useState(false);
    const [dialogEdit, setDialogEdit] = useState(false);
    const [dialogDelete, setDialogDelete] = useState(false);
    const [editingAlmacen, setEditingAlmacen] = useState(null);

    const buildHeaders = useCallback(
        () => ({
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
        }),
        [token]
    );

    const handleApiError = useCallback(
        async (res, fallback) => {
            try {
                const text = await res.text();
                showSnackbar(text || fallback, 'error');
            } catch {
                showSnackbar(fallback, 'error');
            }
        },
        [showSnackbar]
    );

    // ── Almacenes ──
    const getAlmacenes = useCallback(async () => {
        setLoadingAlmacenes(true);
        try {
            const res = await fetch(`${BASE}/almacen/GetAlmacenes?almacen=0`, {
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });
            if (!res.ok) { await handleApiError(res, 'Error al cargar almacenes'); return; }
            const data = await res.json();
            data.forEach((item) => { item.id = item.almacen; });
            setAlmacenes(data);
        } catch {
            showSnackbar('Error al cargar almacenes', 'error');
        } finally {
            setLoadingAlmacenes(false);
        }
    }, [token, handleApiError, showSnackbar]);

    const getExistencias = useCallback(async (almacenId, sku = '') => {
        setLoadingExistencias(true);
        setExistencias([]);
        try {
            const res = await fetch(
                `${BASE}/almacen/GetExistencias?almacen=${almacenId}&SKU=${encodeURIComponent(sku)}`,
                { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
            );
            if (!res.ok) { await handleApiError(res, 'Error al cargar existencias'); return; }
            const data = await res.json();
            data.forEach((item) => {
                item.id = `${item.almacen}-${item.sku}`;
                if (item.unidadInv === null) item.unidadInv = '';
            });
            setExistencias(data);
        } catch {
            showSnackbar('Error al cargar existencias', 'error');
        } finally {
            setLoadingExistencias(false);
        }
    }, [token, handleApiError, showSnackbar]);

    const crearAlmacen = useCallback(async (nombreAlmacen, tipoAlmacen) => {
        setSaving(true);
        try {
            const res = await fetch(`${BASE}/almacen/AlmacenNuevo`, {
                method: 'POST',
                headers: buildHeaders(),
                body: JSON.stringify({ almacen: 0, nombreAlmacen, tipoAlmacen }),
            });
            if (!res.ok) { await handleApiError(res, 'Error al crear almacén'); return; }
            showSnackbar('Almacén creado correctamente');
            setDialogCreate(false);
            await getAlmacenes();
        } catch {
            showSnackbar('Error al crear almacén', 'error');
        } finally {
            setSaving(false);
        }
    }, [buildHeaders, handleApiError, showSnackbar, getAlmacenes]);

    const editarAlmacen = useCallback(async (almacen, nombreAlmacen, tipoAlmacen) => {
        setSaving(true);
        try {
            const res = await fetch(`${BASE}/almacen/EditarAlmacen`, {
                method: 'PUT',
                headers: buildHeaders(),
                body: JSON.stringify({ almacen, nombreAlmacen, tipoAlmacen }),
            });
            if (!res.ok) { await handleApiError(res, 'Error al editar almacén'); return; }
            showSnackbar('Almacén editado correctamente');
            setDialogEdit(false);
            setEditingAlmacen(null);
            await getAlmacenes();
        } catch {
            showSnackbar('Error al editar almacén', 'error');
        } finally {
            setSaving(false);
        }
    }, [buildHeaders, handleApiError, showSnackbar, getAlmacenes]);

    const eliminarAlmacen = useCallback(async (almacenId) => {
        setSaving(true);
        try {
            const res = await fetch(`${BASE}/almacen/EliminarAlmacen?Almacen1=${almacenId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });
            if (!res.ok) { await handleApiError(res, 'No se puede archivar el almacén'); return; }
            showSnackbar('Almacén archivado correctamente');
            setDialogDelete(false);
            if (selectedAlmacen?.almacen === almacenId) {
                setSelectedAlmacen(null);
                setExistencias([]);
            }
            await getAlmacenes();
        } catch {
            showSnackbar('Error al archivar almacén', 'error');
        } finally {
            setSaving(false);
        }
    }, [token, handleApiError, showSnackbar, getAlmacenes, selectedAlmacen]);

    const value = useMemo(() => ({
        almacenes,
        selectedAlmacen, setSelectedAlmacen,
        existencias,
        skuFilter, setSkuFilter,
        loadingAlmacenes, loadingExistencias, saving,
        snackbar, showSnackbar, closeSnackbar,
        dialogCreate, setDialogCreate,
        dialogEdit, setDialogEdit,
        dialogDelete, setDialogDelete,
        editingAlmacen, setEditingAlmacen,
        getAlmacenes, getExistencias,
        crearAlmacen, editarAlmacen, eliminarAlmacen,
    }), [
        almacenes, selectedAlmacen, existencias, skuFilter,
        loadingAlmacenes, loadingExistencias, saving,
        snackbar, showSnackbar, closeSnackbar,
        dialogCreate, dialogEdit, dialogDelete, editingAlmacen,
        getAlmacenes, getExistencias,
        crearAlmacen, editarAlmacen, eliminarAlmacen,
    ]);

    return <InventariosContext.Provider value={value} {...props} />;
}

export function useInventarios() {
    const context = useContext(InventariosContext);
    if (!context) throw new Error('useInventarios debe estar dentro del proveedor InventariosContext');
    return context;
}
